import { useNavigate, Link, Form } from "react-router-dom";
import { useState, useContext } from "react";
import { getAuthToken } from "../../../util/auth";
import { getApiChara } from "../../../util/getApi";
import classes from "./PatientItem.module.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AuthContext from "../../../store/auth-context";
import { FaUser, FaIdCard, FaBirthdayCake, FaVenusMars, FaHeart, FaHandsHelping, FaAddressCard, FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaClinicMedical, FaEdit, FaTrash, FaArrowLeft } from 'react-icons/fa';
import ConfirmModal from "../../UI/ConfirmModal";
import { useTranslation } from "react-i18next";
import { calculateAgeDetailed } from "../../../util/ageCalculator";

function formatDateString(dateString) {
  if (!dateString || dateString === null || dateString === "null") {
    return "";
  }
  const dateObject = new Date(dateString);
  const year = dateObject.getFullYear();
  const month = String(dateObject.getMonth() + 1).padStart(2, "0");
  const day = String(dateObject.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const calculateAge = (dobString) => {
  const { years } = calculateAgeDetailed(dobString);
  return years;
};

function PatientItem({ PatientInfo }) {
  const { t } = useTranslation();
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const formattedDate = PatientInfo ? formatDateString(PatientInfo?.dob) : "";
  const ageDetails = PatientInfo ? calculateAgeDetailed(PatientInfo?.dob) : { years: "", text: "" };
  const calculatedAge = PatientInfo?.age !== undefined && PatientInfo?.age !== null ? PatientInfo.age : ageDetails.years;
  const ageText = ageDetails.text;

  const startDeleteHandler = (event) => {
    event.preventDefault();
    setShowDeleteConfirm(true);
  }

  const confirmDeleteHandler = async () => {
    setIsDeleting(true);

    try {
      const tenantOrClientId = Number(authCtx.tenantOrClientId);
      const id = PatientInfo ? PatientInfo.patientId : '';
      const token = getAuthToken();
      if (token === null || token === 'EXPIRED') {
        navigate('/');
      }

      const apiChar = getApiChara();
      const apiUrl = `${apiChar}/patient/deletePatientById?id=${id}&tenantOrClientId=${tenantOrClientId}`;

      const response = await fetch(apiUrl, {
        method: 'DELETE',
        headers: {
          'Authorization': 'Bearer ' + token
        }
      });

      if (!response.ok) {
        toast.error(t("Could not delete patient"));
        throw new Error("Could not delete patient");
      } else {
        setIsDeleting(false);
        toast.success(t("Patient deleted successfully!"));
        navigate('..');
      }
    } catch (error) {
      console.error("Error deleting patient:", error);
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  }

  return (
    <>
      <article className={classes.patient}>
        <h3 className='textHead' style={{ color: 'var(--primary-color)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px' }}>
          <FaUser className="me-2" /> {PatientInfo?.firstName + " " + (PatientInfo?.middleName ? PatientInfo.middleName + " " : "") + PatientInfo?.lastName}
        </h3>
        <div className={classes.cardview}>
          <Form method="post" className={classes.form}>

            <div className={classes.sectionHeader}>
              <FaIdCard /> {t("Personal Information")}
            </div>

            <div className="row">
              <div className="col-md-4 col-6">
                <label className={classes.label} htmlFor="patientId">{t("Patient Id")}</label>
                <input id="patientId" type="text" name="patientId" required defaultValue={PatientInfo ? PatientInfo?.patientId : ''} disabled />
              </div>
              <div className="col-md-4 col-6">
                <label className={classes.label} htmlFor="firstName">{t("First Name")}</label>
                <input id="firstName" type="text" name="firstName" required defaultValue={PatientInfo ? PatientInfo?.firstName : ''} disabled />
              </div>
              <div className="col-md-4 col-6">
                <label className={classes.label} htmlFor="middleName">{t("Middle Name")}</label>
                <input id="middleName" type="text" name="middleName" required defaultValue={PatientInfo ? PatientInfo?.middleName : ''} disabled />
              </div>
              <div className="col-md-4 col-6">
                <label className={classes.label} htmlFor="lastName">{t("Last Name")}</label>
                <input id="lastName" type="text" name="lastName" required defaultValue={PatientInfo ? PatientInfo?.lastName : ''} disabled />
              </div>
              <div className="col-md-4 col-6">
                <label className={classes.label} htmlFor="nickName">{t("Nick Name")}</label>
                <input id="nickName" type="text" name="nickName" required defaultValue={PatientInfo ? PatientInfo?.nickName : ''} disabled />
              </div>
              <div className="col-md-4 col-6">
                <label className={classes.label} htmlFor="dob">{t("Date of Birth")}</label>
                <input id="dob" type="date" name="dob" defaultValue={formattedDate} disabled />
              </div>
              <div className="col-md-4 col-6">
                <label className={classes.label} htmlFor="gender">{t("Gender")}</label>
                <select id="gender" name="gender" defaultValue={PatientInfo ? PatientInfo?.gender : ''} disabled>
                  <option value="male">{t("Male")}</option>
                  <option value="female">{t("Female")}</option>
                  <option value="other">{t("Other")}</option>
                </select>
              </div>
              <div className="col-md-4 col-6">
                <label className={classes.label} htmlFor="age">{t("Age (Years)")}</label>
                <div className="d-flex align-items-center gap-2">
                  <input id="age" type="number" name="age" required value={calculatedAge} disabled style={{ flex: 1 }} />
                  {ageText && <span className="text-primary fw-bold small" style={{ whiteSpace: 'nowrap' }}>{ageText}</span>}
                </div>
              </div>
              <div className="col-md-4 col-6">
                <label className={classes.label} htmlFor="maritalStatus">{t("Marital Status")}</label>
                <select id="maritalStatus" name="maritalStatus" defaultValue={PatientInfo ? PatientInfo?.maritalStatus : ''} disabled>
                  <option value="single">{t("Single")}</option>
                  <option value="married">{t("Married")}</option>
                  <option value="divorced">{t("Divorced")}</option>
                  <option value="widowed">{t("Widowed")}</option>
                </select>
              </div>
            </div>

            <div className={classes.sectionHeader}>
              <FaAddressCard /> {t("Identity & Background")}
            </div>
            <div className="row">
              <div className="col-md-4 col-6">
                <label className={classes.label} htmlFor="religion">{t("Religion")}</label>
                <input id="religion" type="text" name="religion" defaultValue={PatientInfo ? PatientInfo?.religion : ''} disabled />
              </div>
              <div className="col-md-4 col-6">
                <label className={classes.label} htmlFor="caste">{t("Caste")}</label>
                <input id="caste" type="text" name="caste" defaultValue={PatientInfo ? PatientInfo?.caste : ''} disabled />
              </div>
              <div className="col-md-4 col-6">
                <label className={classes.label} htmlFor="occupation">{t("Occupation")}</label>
                <input id="occupation" type="text" name="occupation" defaultValue={PatientInfo ? PatientInfo?.occupation : ''} disabled />
              </div>
              <div className="col-md-4 col-6">
                <label className={classes.label} htmlFor="panCardNo">{t("PAN Card No")}</label>
                <input id="panCardNo" type="text" name="panCardNo" defaultValue={PatientInfo ? PatientInfo?.panCardNo : ''} disabled />
              </div>
              <div className="col-md-4 col-6">
                <label className={classes.label} htmlFor="adharCardNo">{t("Aadhar Card No")}</label>
                <input id="adharCardNo" type="text" name="adharCardNo" defaultValue={PatientInfo ? PatientInfo?.adharCardNo : ''} disabled />
              </div>
              <div className="col-md-4 col-6">
                <label className={classes.label} htmlFor="identificationMark">{t("Identification Mark")}</label>
                <input id="identificationMark" type="text" name="identificationMark" defaultValue={PatientInfo ? PatientInfo?.identificationMark : ''} disabled />
              </div>
            </div>

            <div className={classes.sectionHeader}>
              <FaPhoneAlt /> {t("Contact Information")}
            </div>
            <div className="row">
              <div className="col-md-4 col-6">
                <label className={classes.label} htmlFor="mobile">{t("Mobile Number")}</label>
                <input id="mobile" type="tel" name="mobile" required defaultValue={PatientInfo ? PatientInfo?.mobile : ''} disabled />
              </div>
              <div className="col-md-4 col-6">
                <label className={classes.label} htmlFor="alternateMobileNum">{t("Alt Mobile No")}</label>
                <input id="alternateMobileNum" type="tel" name="alternateMobileNum" defaultValue={PatientInfo ? PatientInfo?.alternateMobileNum : ''} disabled />
              </div>
              <div className="col-md-4 col-6">
                <label className={classes.label} htmlFor="emailId">{t("Email")}</label>
                <input id="emailId" type="email" name="emailId" defaultValue={PatientInfo ? PatientInfo?.emailId : ''} disabled />
              </div>
            </div>

            <div className={classes.sectionHeader}>
              <FaMapMarkerAlt /> {t("Address & Reference")}
            </div>
            <div className="row">
              <div className="col-md-4 col-6">
                <label className={classes.label} htmlFor="address">{t("Address")}</label>
                <input id="address" type="text" name="address" required defaultValue={PatientInfo ? PatientInfo?.address : ''} disabled />
              </div>
              <div className="col-md-4 col-12">
                <div className="row">
                  <div className="col-6">
                    <label className={classes.label} htmlFor="city">{t("City")}</label>
                    <input id="city" type="text" name="city" defaultValue={PatientInfo ? PatientInfo?.city : ''} disabled />
                  </div>
                  <div className="col-6">
                    <label className={classes.label} htmlFor="pincode">{t("Pincode")}</label>
                    <input id="pincode" type="text" name="pincode" defaultValue={PatientInfo ? PatientInfo?.pincode : ''} disabled />
                  </div>
                </div>
              </div>
              <div className="col-md-4 col-12">
                <label className={classes.label} htmlFor="referredBy">{t("Referred By")}</label>
                <input id="referredBy" type="text" name="referredBy" defaultValue={PatientInfo ? PatientInfo?.referredBy : ''} disabled />
              </div>
              <div className="col-md-4 col-12">
                <label className={classes.label} htmlFor="registrationNo">{t("Registration No")}</label>
                <input id="registrationNo" type="text" name="registrationNo" defaultValue={PatientInfo ? PatientInfo?.registrationNo : ''} disabled />
              </div>
            </div>

            <div className={classes.actions}>
              <Link className={classes.editBtn} to="edit">
                <FaEdit className="me-2" /> {t("Edit Patient")}
              </Link>
              <button
                type="button"
                className={classes.deleteBtn}
                onClick={startDeleteHandler}
                disabled={isDeleting}
              >
                <FaTrash className="me-2" /> {isDeleting ? t("Deleting...") : t('Delete')}
              </button>
              <Link className={classes.backBtn} to="..">
                <FaArrowLeft className="me-2" /> {t("Back to List")}
              </Link>
            </div>
          </Form>
        </div>
      </article>
      <ConfirmModal
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDeleteHandler}
        title={t("Delete Patient")}
        message={t("Are you sure you want to delete {{name}}? This action cannot be undone.", { name: `${PatientInfo?.firstName} ${PatientInfo?.lastName}` })}
        variant="danger"
        isLoading={isDeleting}
      />
    </>
  );
}

export default PatientItem;
